import * as React from "react";
import { useQuery } from "@tanstack/react-query";

import {
  AnalyticsRange,
  BillingOverviewRow,
  billingKeys,
  fetchBillingAnalytics,
  fetchBillingOverview,
} from "api/billing";
import { shouldRetryRequest } from "api/apiError";
import { isConnected } from "Components/Tenants";
import { OverviewFilter, OverviewFilterOption } from "./types";

export const OVERVIEW_FILTERS: OverviewFilterOption[] = [
  { id: "all", label: "All", matches: () => true },
  { id: "due_soon", label: "Due soon", matches: (row) => row.status === "due_soon" },
  { id: "overdue", label: "Overdue", matches: (row) => row.status === "overdue" },
  { id: "read_only", label: "Read-only", matches: (row) => row.status === "read_only" },
  { id: "paused", label: "Paused", matches: (row) => row.status === "paused" },
  { id: "not_connected", label: "Not connected", matches: (row) => !isConnected(row.link) },
  { id: "paid_up", label: "Paid up", matches: (row) => row.status === "paid_up" },
];

const EMPTY_ROWS: BillingOverviewRow[] = [];

/** Shared query for the portfolio overview (also used by the Clients list billing column). */
export const useBillingOverviewQuery = (enabled = true) =>
  useQuery({
    queryKey: billingKeys.overview(),
    queryFn: fetchBillingOverview,
    retry: shouldRetryRequest,
    staleTime: 30 * 1000,
    enabled,
  });

/**
 * Collections in `range` plus today's outstanding (the reports behind the overview tiles). The
 * previous period stays on screen while another one loads.
 */
export const useBillingAnalyticsQuery = (range: AnalyticsRange, enabled = true) =>
  useQuery({
    queryKey: billingKeys.analytics(range),
    queryFn: () => fetchBillingAnalytics(range),
    retry: shouldRetryRequest,
    staleTime: 30 * 1000,
    keepPreviousData: true,
    enabled,
  });

const useBillingOverviewData = () => {
  const [filter, setFilter] = React.useState<OverviewFilter>("all");
  const [search, setSearch] = React.useState<string>("");
  const query = useBillingOverviewQuery();
  const rows = query.data ? query.data.rows : EMPTY_ROWS;

  const counts = React.useMemo(() => {
    const result: Record<OverviewFilter, number> = {
      all: 0,
      due_soon: 0,
      overdue: 0,
      read_only: 0,
      paused: 0,
      not_connected: 0,
      paid_up: 0,
    };
    OVERVIEW_FILTERS.forEach((option) => {
      result[option.id] = rows.filter(option.matches).length;
    });
    return result;
  }, [rows]);

  const visibleRows = React.useMemo(() => {
    const option = OVERVIEW_FILTERS.find((candidate) => candidate.id === filter);
    const term = search.trim().toLowerCase();
    return (
      rows
        .filter(
          (row) =>
            (!option || option.matches(row)) &&
            (!term ||
              `${row.tenantName || ""} ${row.slug || ""} ${row.uri || ""}`
                .toLowerCase()
                .includes(term))
        )
        // Connected clients first (the others are greyed out), then the server's urgency order.
        .sort((a, b) => Number(isConnected(b.link)) - Number(isConnected(a.link)))
    );
  }, [rows, filter, search]);

  // Until the production release only Actionable (beta) clients accept billing changes.
  const actionableRows = React.useMemo(
    () => visibleRows.filter((row) => row.stage === "beta"),
    [visibleRows]
  );
  const lockedRows = React.useMemo(
    () => visibleRows.filter((row) => row.stage !== "beta"),
    [visibleRows]
  );

  return {
    states: {
      filter,
      search,
      rows,
      visibleRows,
      actionableRows,
      lockedRows,
      counts,
      kpis: query.data ? query.data.kpis : null,
      loading: query.isLoading,
      refreshing: query.isFetching && !query.isLoading,
      error: query.error,
    },
    setFilter,
    setSearch,
    refetch: query.refetch,
  };
};

export default useBillingOverviewData;
